import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut,
  Download,
  Package,
  PoundSterling,
  Loader2,
  FileText,
  ChevronDown,
  ChevronRight,
  Archive,
  RotateCcw,
  Percent,
  Plus,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, gbp, LOGO_URL } from "@/lib/api";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ORDER_STATUSES = ["Received", "In Progress", "Ready"];
const STATUS_STYLES = {
  Received: "bg-amber-100 text-amber-800",
  "In Progress": "bg-blue-100 text-blue-800",
  Ready: "bg-green-100 text-green-800",
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({ order_count: 0, total_revenue: 0 });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [view, setView] = useState("active");
  const [discounts, setDiscounts] = useState([]);
  const [newCode, setNewCode] = useState("");
  const [newPercent, setNewPercent] = useState(10);
  const [creatingDiscount, setCreatingDiscount] = useState(false);

  const canDelete = user?.email?.toLowerCase() === "support@nlcsshop.co.uk";

  const confirmArchive = async () => {
    if (!archiveTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/orders/${archiveTarget.id}`);
      setOrders((prev) => prev.filter((o) => o.id !== archiveTarget.id));
      setStats((s) => ({
        ...s,
        order_count: Math.max(0, s.order_count - 1),
        total_revenue: Math.round((s.total_revenue - (archiveTarget.total || 0)) * 100) / 100,
        archived_count: (s.archived_count || 0) + 1,
      }));
      toast.success("Order archived");
      setArchiveTarget(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to archive order");
    } finally {
      setDeleting(false);
    }
  };

  const restoreOrder = async (o) => {
    try {
      await api.post(`/admin/orders/${o.id}/restore`);
      setOrders((prev) => prev.filter((x) => x.id !== o.id));
      setStats((s) => ({
        ...s,
        order_count: s.order_count + 1,
        total_revenue: Math.round((s.total_revenue + (o.total || 0)) * 100) / 100,
        archived_count: Math.max(0, (s.archived_count || 0) - 1),
      }));
      toast.success("Order restored");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to restore order");
    }
  };

  const updateStatus = async (orderId, status) => {
    try {
      await api.patch(`/admin/orders/${orderId}/status`, { status });
      setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, status } : o)));
      toast.success(`Status set to ${status}`);
    } catch (e) {
      toast.error("Failed to update status");
    }
  };

  const createDiscount = async () => {
    const code = newCode.trim().toUpperCase();
    const percent = parseInt(newPercent, 10);
    if (!code || !percent) {
      toast.error("Enter a code and a percentage");
      return;
    }
    setCreatingDiscount(true);
    try {
      const res = await api.post("/admin/discounts", { code, percent });
      setDiscounts((p) => [res.data, ...p]);
      setNewCode("");
      setNewPercent(10);
      toast.success(`Code ${res.data.code} created`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create code");
    } finally {
      setCreatingDiscount(false);
    }
  };

  const deleteDiscount = async (id) => {
    try {
      await api.delete(`/admin/discounts/${id}`);
      setDiscounts((p) => p.filter((d) => d.id !== id));
      toast.success("Code removed");
    } catch (e) {
      toast.error("Failed to remove code");
    }
  };

  useEffect(() => {
    if (user === false) {
      navigate("/admin");
      return;
    }
    if (user) {
      setLoading(true);
      Promise.all([
        api.get(`/admin/orders${view === "archived" ? "?archived=true" : ""}`),
        api.get("/admin/stats"),
      ])
        .then(([o, s]) => {
          setOrders(o.data);
          setStats(s.data);
        })
        .catch(() => toast.error("Failed to load orders"))
        .finally(() => setLoading(false));
    }
  }, [user, navigate, view]);

  useEffect(() => {
    if (user) {
      api.get("/admin/discounts").then((r) => setDiscounts(r.data)).catch(() => {});
    }
  }, [user]);

  const downloadFile = async (fileId, filename) => {
    setDownloading(fileId);
    try {
      const res = await api.get(`/admin/files/${fileId}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed");
    } finally {
      setDownloading(null);
    }
  };

  const fmtDate = (iso) =>
    new Date(iso).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="animate-spin text-[var(--brand-primary)]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F8]" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b border-black/10 bg-white">
        <div className="flex items-center justify-between px-6 py-4 lg:px-10">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="NLCS logo" className="h-9 w-9 object-contain" />
            <div>
              <div className="font-display text-lg font-extrabold leading-none">NLCS Admin</div>
              <div className="text-xs text-[var(--text-secondary)]">{user?.email}</div>
            </div>
          </div>
          <button
            onClick={() => {
              logout();
              navigate("/admin");
            }}
            data-testid="logout-button"
            className="inline-flex items-center gap-2 border border-black/15 px-4 py-2 text-sm font-medium transition-colors hover:bg-black/5"
          >
            <LogOut size={15} /> Sign out
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-6 py-8 lg:px-10">
        {/* Stats */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex items-center gap-4 border border-black/10 bg-white p-6">
            <span className="flex h-12 w-12 items-center justify-center bg-[var(--brand-primary)]/10 text-[var(--brand-primary)]">
              <Package size={22} />
            </span>
            <div>
              <div className="font-display text-3xl font-extrabold" data-testid="stat-orders">
                {stats.order_count}
              </div>
              <div className="text-sm text-[var(--text-secondary)]">Total orders</div>
            </div>
          </div>
          <div className="flex items-center gap-4 border border-black/10 bg-white p-6">
            <span className="flex h-12 w-12 items-center justify-center bg-[var(--brand-primary)]/10 text-[var(--brand-primary)]">
              <PoundSterling size={22} />
            </span>
            <div>
              <div className="font-display text-3xl font-extrabold" data-testid="stat-revenue">
                {gbp(stats.total_revenue)}
              </div>
              <div className="text-sm text-[var(--text-secondary)]">Total revenue</div>
            </div>
          </div>
        </div>

        {/* Discount codes */}
        <div className="mt-8 border border-black/10 bg-white" data-testid="discounts-panel">
          <div className="flex items-center gap-2 border-b border-black/10 px-6 py-4">
            <Percent size={18} className="text-[var(--brand-primary)]" />
            <h2 className="font-display text-xl font-bold">Discount codes</h2>
          </div>
          <div className="p-6">
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[160px]">
                <label className="text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
                  Code
                </label>
                <input
                  value={newCode}
                  onChange={(e) => setNewCode(e.target.value.toUpperCase())}
                  onKeyDown={(e) => e.key === "Enter" && createDiscount()}
                  placeholder="E.g. SAVE10"
                  data-testid="new-discount-code"
                  className="mt-1 w-full border border-black/15 px-3 py-2 text-sm uppercase outline-none focus:border-[var(--brand-primary)]"
                />
              </div>
              <div className="w-28">
                <label className="text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
                  % off
                </label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={newPercent}
                  onChange={(e) => setNewPercent(e.target.value)}
                  data-testid="new-discount-percent"
                  className="mt-1 w-full border border-black/15 px-3 py-2 text-sm outline-none focus:border-[var(--brand-primary)]"
                />
              </div>
              <button
                onClick={createDiscount}
                disabled={creatingDiscount}
                data-testid="add-discount-button"
                className="inline-flex items-center gap-2 bg-[var(--brand-primary)] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[var(--brand-primary-hover)] disabled:opacity-50"
              >
                {creatingDiscount ? <Loader2 className="animate-spin" size={15} /> : <Plus size={15} />} Add code
              </button>
            </div>

            {discounts.length === 0 ? (
              <p className="mt-5 text-sm text-[var(--text-secondary)]">
                No discount codes yet. Add one above — customers enter it on the order page.
              </p>
            ) : (
              <div className="mt-5 flex flex-wrap gap-2" data-testid="discounts-list">
                {discounts.map((d) => (
                  <div
                    key={d.id}
                    data-testid={`discount-${d.code}`}
                    className="flex items-center gap-3 border border-black/15 bg-[#F9F9F8] px-3 py-2"
                  >
                    <span className="font-mono text-sm font-bold">{d.code}</span>
                    <span className="bg-[var(--brand-primary)]/10 px-2 py-0.5 text-xs font-semibold text-[var(--brand-primary)]">
                      {d.percent}% off
                    </span>
                    <button
                      onClick={() => deleteDiscount(d.id)}
                      data-testid={`remove-discount-${d.code}`}
                      className="text-[var(--text-secondary)] transition-colors hover:text-red-600"
                      aria-label="Remove code"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Orders table */}
        <div className="mt-8 border border-black/10 bg-white">
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-black/10 px-6 py-4">
            <h2 className="font-display text-xl font-bold">
              {view === "archived" ? "Archived orders" : "Customer orders"}
            </h2>
            <div className="flex items-center border border-black/10">
              <button
                onClick={() => { setExpanded(null); setView("active"); }}
                data-testid="tab-active"
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  view === "active" ? "bg-[var(--brand-primary)] text-white" : "hover:bg-black/5"
                }`}
              >
                Active
              </button>
              <button
                onClick={() => { setExpanded(null); setView("archived"); }}
                data-testid="tab-archived"
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  view === "archived" ? "bg-[var(--brand-primary)] text-white" : "hover:bg-black/5"
                }`}
              >
                Archived{stats.archived_count ? ` (${stats.archived_count})` : ""}
              </button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="animate-spin text-[var(--brand-primary)]" />
            </div>
          ) : orders.length === 0 ? (
            <p className="px-6 py-16 text-center text-[var(--text-secondary)]">
              {view === "archived" ? "No archived orders." : "No orders yet."}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="orders-table">
                <thead>
                  <tr className="border-b border-black/10 bg-[#F9F9F8] text-left text-xs uppercase tracking-wide text-[var(--text-secondary)]">
                    <th className="px-4 py-3 font-semibold">Order</th>
                    <th className="px-4 py-3 font-semibold">Customer</th>
                    <th className="px-4 py-3 font-semibold">Date</th>
                    <th className="px-4 py-3 font-semibold">Status</th>
                    <th className="px-4 py-3 font-semibold">Payment</th>
                    <th className="px-4 py-3 text-right font-semibold">Total</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <React.Fragment key={o.id}>
                      <tr
                        className="cursor-pointer border-b border-black/5 hover:bg-black/[0.02]"
                        onClick={() => setExpanded(expanded === o.id ? null : o.id)}
                        data-testid={`order-row-${o.order_number}`}
                      >
                        <td className="px-4 py-3 font-mono text-xs font-semibold">{o.order_number}</td>
                        <td className="px-4 py-3">
                          <div className="font-medium">{o.name}</div>
                          <div className="text-xs text-[var(--text-secondary)]">{o.email}</div>
                        </td>
                        <td className="px-4 py-3 text-[var(--text-secondary)]">{fmtDate(o.created_at)}</td>
                        <td className="px-4 py-3">
                          <span
                            data-testid={`status-badge-${o.order_number}`}
                            className={`inline-block px-2 py-0.5 text-xs font-semibold ${
                              STATUS_STYLES[o.status] || STATUS_STYLES.Received
                            }`}
                          >
                            {o.status || "Received"}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-block px-2 py-0.5 text-xs font-medium ${
                              o.payment_status === "COMPLETED"
                                ? "bg-green-100 text-green-800"
                                : "bg-amber-100 text-amber-800"
                            }`}
                          >
                            {o.payment_status}
                          </span>
                          {o.payment_mode === "simulated" && (
                            <span className="ml-1 text-[10px] text-[var(--text-secondary)]">(test)</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right font-mono font-semibold">{gbp(o.total)}</td>
                        <td className="px-4 py-3 text-[var(--text-secondary)]">
                          {expanded === o.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        </td>
                      </tr>
                      {expanded === o.id && (
                        <tr className="border-b border-black/10 bg-[#F9F9F8]">
                          <td colSpan={7} className="px-6 py-5">
                            {view === "active" && (
                              <div className="mb-5 flex flex-wrap items-center gap-3 border-b border-black/10 pb-4">
                                <span className="text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
                                  Order status
                                </span>
                                <div className="flex items-center gap-1">
                                  {ORDER_STATUSES.map((st) => (
                                    <button
                                      key={st}
                                      onClick={() => updateStatus(o.id, st)}
                                      data-testid={`set-status-${st.replace(/\s+/g, "-")}-${o.order_number}`}
                                      className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                                        (o.status || "Received") === st
                                          ? STATUS_STYLES[st]
                                          : "border border-black/15 bg-white hover:bg-black/5"
                                      }`}
                                    >
                                      {st}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                            <div className="grid gap-6 md:grid-cols-3">
                              <div>
                                <h4 className="text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
                                  Contact
                                </h4>
                                <p className="mt-2 text-sm">{o.name}</p>
                                <p className="text-sm">{o.phone}</p>
                                <p className="text-sm">{o.email}</p>
                                {o.delivery_method ? (
                                  <div className="mt-3 border-t border-black/10 pt-2">
                                    <span className="text-xs font-semibold uppercase tracking-wide text-[var(--brand-primary)]">
                                      📮 Post to
                                    </span>
                                    <p className="mt-1 text-sm">
                                      {[o.delivery_address?.line1, o.delivery_address?.city, o.delivery_address?.postcode, o.delivery_address?.country]
                                        .filter(Boolean)
                                        .join(", ")}
                                    </p>
                                    <p className="text-xs text-[var(--text-secondary)]">
                                      {o.delivery_label} ({gbp(o.delivery_fee)})
                                    </p>
                                  </div>
                                ) : (
                                  <p className="mt-3 text-xs text-[var(--text-secondary)]">Collection from shop</p>
                                )}
                              </div>
                              <div>
                                <h4 className="text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
                                  Services
                                </h4>
                                <ul className="mt-2 space-y-1 text-sm">
                                  {o.services.map((s, i) => (
                                    <li key={i} className="flex justify-between gap-4">
                                      <span>
                                        {s.label} × {s.quantity}
                                      </span>
                                      <span className="font-mono">{gbp(s.line_total)}</span>
                                    </li>
                                  ))}
                                </ul>
                                {o.instructions && (
                                  <p className="mt-3 border-l-2 border-[var(--brand-primary)] pl-3 text-sm italic text-[var(--text-secondary)]">
                                    "{o.instructions}"
                                  </p>
                                )}
                              </div>
                              <div>
                                <h4 className="text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
                                  Uploaded files
                                </h4>
                                {o.files.length === 0 ? (
                                  <p className="mt-2 text-sm text-[var(--text-secondary)]">No files.</p>
                                ) : (
                                  <ul className="mt-2 space-y-2">
                                    {o.files.map((f) => (
                                      <li key={f.id}>
                                        <button
                                          onClick={() => downloadFile(f.id, f.original_filename)}
                                          data-testid={`download-file-${f.id}`}
                                          className="inline-flex max-w-full items-center gap-2 border border-black/15 bg-white px-3 py-2 text-left text-sm transition-colors hover:border-[var(--brand-primary)]"
                                        >
                                          {downloading === f.id ? (
                                            <Loader2 size={14} className="animate-spin" />
                                          ) : (
                                            <FileText size={14} className="text-[var(--brand-primary)]" />
                                          )}
                                          <span className="truncate">{f.original_filename}</span>
                                          <Download size={13} className="ml-auto shrink-0" />
                                        </button>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            </div>
                            {canDelete && (
                              <div className="mt-6 border-t border-black/10 pt-4">
                                {view === "archived" ? (
                                  <button
                                    onClick={() => restoreOrder(o)}
                                    data-testid={`restore-order-${o.order_number}`}
                                    className="inline-flex items-center gap-2 border border-black/15 bg-white px-4 py-2 text-sm font-medium transition-colors hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)]"
                                  >
                                    <RotateCcw size={15} /> Restore order
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => setArchiveTarget(o)}
                                    data-testid={`delete-order-${o.order_number}`}
                                    className="inline-flex items-center gap-2 border border-red-200 bg-red-50 px-4 py-2 text-sm font-medium text-red-700 transition-colors hover:bg-red-100"
                                  >
                                    <Archive size={15} /> Archive order
                                  </button>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <AlertDialog open={!!archiveTarget} onOpenChange={(o) => !o && setArchiveTarget(null)}>
        <AlertDialogContent data-testid="delete-order-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this order?</AlertDialogTitle>
            <AlertDialogDescription>
              Order {archiveTarget?.order_number} ({archiveTarget?.name}) will be moved to the
              Archived tab. You can restore it any time — nothing is permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-confirm"
              onClick={confirmArchive}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {deleting ? "Archiving…" : "Archive"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
